import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/ui/Button/Button';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import useModalClass from 'hooks/useModalClass';
import { createPortal } from 'react-dom';
import { showError, showSuccess } from 'utils/utils';
import withErrorBoundary from '../../withErrorBoundary';
import MoRoomsTab from './MoRoomsTab';
import MoPaxesTab from './MoPaxesTab';
import { MoSegmentEditorOtherResponse, MyOrder, Pax } from 'ts/shared/interfaces/MOResponse';
import { changeSegmentEditor, segmentEditorOther } from 'services/myOrders';
import { getStatus } from 'constants/mo';
import ScreenshotSupport from '../../ScreenshotSupport';
import MaximizeModal from '../../MaximizeModal';
import useDraggableModal from 'hooks/useDraggableModal';
import Tabs from 'components/ui/Tabs/Tabs';

interface MoRoomsAndPaxesModalProps {
  myOrder?: MyOrder;
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  onSubmit: (myOrder: MyOrder) => void;
}

const MoRoomsAndPaxesModal: FC<MoRoomsAndPaxesModalProps> = ({
  myOrder,
  openModal,
  setOpenModal,
  ...props
}) => {
  const [t] = useTranslation();
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const [segmentEditorOrder, setSegmentEditorOrder] = useState<MoSegmentEditorOtherResponse | undefined>();
  const classList = useModalClass(openModal);
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);
  const [activeRoomIndex, setActiveRoomIndex] = useState(0);
  const [activePaxIndex, setActivePaxIndex] = useState(0);
  const [paxesModel, setPaxesModel] = useState<Pax[]>([]);
  const [roomsModel, setRoomsModel] = useState<MyOrder['fullSegment']['rooms']>([]);
  const [orderModel, setOrderModel] = useState<any>(myOrder?.order || null);
  const [isLoading, setIsLoading] = useState(false);
  const areRoomsVisible = myOrder?.fullSegment?.rooms
    .map((room, realIndex) => ({ room, realIndex }))
    .some(({ room }) => (room.delRow || 0) === 0);
  const [activeTab, setActiveTab] = useState(areRoomsVisible ? 0 : 1);
  const segment = orderModel?.segments.find((s: any) => s.orderSegId === myOrder?.fullSegment.orderSegId);

  useEffect(() => {
    if (openModal && myOrder) {
      const nextOrderModel = myOrder.order;
      const segmentFromOrder = nextOrderModel.segments.find(s => s.orderSegId === myOrder.fullSegment.orderSegId);
      const paxes = nextOrderModel.paxes || [];
      const rooms = segmentFromOrder?.rooms || [];
      setOrderModel(nextOrderModel);
      setPaxesModel(paxes);
      setRoomsModel(rooms);
      const firstVisibleRoomIndex = rooms.findIndex((room: any) => (room.delRow || 0) === 0);
      setActiveRoomIndex(activeRoomIndex >= 0 ? activeRoomIndex : firstVisibleRoomIndex);
      setActivePaxIndex(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myOrder, openModal]);

  useEffect(() => {
    if (openModal) {
      (async () => {
        const nextSegmentEditorOrder = await segmentEditorOther();
        setSegmentEditorOrder(nextSegmentEditorOrder);
      })();
    }
  }, [openModal]);

  const syncFromResult = (result: MyOrder, preferredRoomIndex?: number) => {
    const nextOrderModel = result.order;
    const segmentFromOrder = nextOrderModel.segments.find((s: any) => s.orderSegId === result.fullSegment.orderSegId);
    const nextRooms = segmentFromOrder?.rooms || [];
    setOrderModel(nextOrderModel);
    setPaxesModel(nextOrderModel.paxes || []);
    setRoomsModel(nextRooms);
    if (
      typeof preferredRoomIndex === 'number' &&
      preferredRoomIndex >= 0 &&
      preferredRoomIndex < nextRooms.length &&
      (nextRooms[preferredRoomIndex]?.delRow || 0) === 0
    ) {
      setActiveRoomIndex(preferredRoomIndex);
      return;
    }
    if (
      activeRoomIndex >= 0 &&
      activeRoomIndex < nextRooms.length &&
      (nextRooms[activeRoomIndex]?.delRow || 0) === 0
    ) {
      setActiveRoomIndex(activeRoomIndex);
      return;
    }
    const firstVisibleRoomIndex = nextRooms.findIndex((room: any) => (room.delRow || 0) === 0);
    setActiveRoomIndex(firstVisibleRoomIndex >= 0 ? firstVisibleRoomIndex : 0);
  };

  const onClickSave = async () => {
    if (!myOrder || !orderModel || !segment) return;
    setIsLoading(true);
    const result = await changeSegmentEditor({
      order: orderModel,
      changePrice: true,
    });
    if (result) {
      showSuccess(t('mo.segment_gross_edited'));
      props.onSubmit({ ...result, orderRow: { ...result.orderRow, status: myOrder.orderRow.status } });
    } else {
      showError(t('something_went_wrong'));
    }
    setIsLoading(false);
  };

  return createPortal(
    <div
      className="modal-overlay modal-overlay-top mo-message-modal-overlay modal-maximizable" style={{ direction: 'ltr' }}
      ref={modalOverlayRef}
    >
      <div ref={modalRef} className={classList + ' modal mo-message-modal'} style={modalStyle}>
        <div
          className={'modal-title draggable-modal-handle' + (isDragging ? ' dragging' : '')}
          {...dragHandleProps}
        >
          <h5 className="title">
            Segment Editor Rooms & Paxes{' '}
            {myOrder?.fullSegment && (
              <Fragment>
                {' '}-{' '}Order: {myOrder.orderRow.orderId}{' '}
                Segment:
                {myOrder.fullSegment.orderSegId}{' '}
                <span className={`mo-status ${getStatus(myOrder.orderRow.status)}`}>
                  {myOrder.orderRow.status}
                </span>
              </Fragment>
            )}
          </h5>
          <div data-no-drag="true">
            <ScreenshotSupport />
            <MaximizeModal modalOverlayRef={modalOverlayRef} />
            <FontAwesomeIcon icon={faTimes} onClick={() => setOpenModal(false)} />
          </div>
        </div>

        <div className="modal-body modal-body-overflow">
          {areRoomsVisible && (
            <Tabs
        size="small"
              label="Rooms and paxes"
              value={String(activeTab)}
              onValueChange={(v) => setActiveTab(Number(v))}
              className="room-tabs"
              items={[
                { value: '0', label: 'Rooms' },
                { value: '1', label: 'Paxes' },
              ]}
            />
          )}

          {areRoomsVisible && activeTab === 0 && (
            <MoRoomsTab
              myOrder={myOrder}
              orderModel={orderModel}
              setOrderModel={setOrderModel}
              paxesModel={paxesModel}
              roomsModel={roomsModel}
              setRoomsModel={setRoomsModel}
              activeRoomIndex={activeRoomIndex}
              setActiveRoomIndex={setActiveRoomIndex}
              segmentEditorOrder={segmentEditorOrder}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              syncFromResult={syncFromResult}
              onSubmit={props.onSubmit}
            />
          )}
          {activeTab === 1 && (
            <MoPaxesTab
              myOrder={myOrder}
              orderModel={orderModel}
              setOrderModel={setOrderModel}
              paxesModel={paxesModel}
              setPaxesModel={setPaxesModel}
              activePaxIndex={activePaxIndex}
              setActivePaxIndex={setActivePaxIndex}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              syncFromResult={syncFromResult}
              onSubmit={props.onSubmit}
            />
          )}
        </div>

        <div className="modal-footer">
          <Button disabled={isLoading} title={t('save')} className="btn btn-primary ml-0 mr-a" onClick={onClickSave} />
          <Button
            title={t('cancel')}
            className="btn btn-default"
            onClick={() => setOpenModal(false)}
          />
        </div>
      </div>
    </div>
    , document.body);
};

export default withErrorBoundary(MoRoomsAndPaxesModal, 'MoRoomsAndPaxesModal');
